import { axiosInstance } from '../../../shared/api';

/**
 * Fetch all notifications (recurring and goal notifications)
 * @param {string} status - Filter by status (pending, sent, accepted, declined, read)
 * @param {number} limit - Number of records to return (1-100)
 * @param {number} offset - Pagination offset
 * @param {string} language - Language code for Accept-Language sidebar-header (en, hy, ru)
 * @returns {Promise<{notifications: Array, total: number}>}
 */
export const fetchNotifications = async (
	status = 'pending',
	limit = 50,
	offset = 0,
	language = 'en',
) => {
	try {
		const params = new URLSearchParams();
		if (status) params.append('status', status);
		if (limit) params.append('limit', limit.toString());
		if (offset) params.append('offset', offset.toString());

		const response = await axiosInstance.get(
			`notifications?${params.toString()}`,
			{
				withCredentials: true,
				headers: {
					Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
					'Accept-Language': language,
				},
			},
		);

		return response.data;
	} catch (error) {
		console.error('Error fetching notifications:', error);
		if (error.response) {
			const errorMessage =
				error.response.data?.detail ||
				error.response.data?.message ||
				`Server error: ${error.response.status}` ||
				'Failed to fetch notifications';
			throw new Error(errorMessage);
		} else if (error.request) {
			throw new Error('No response from server. Please check your connection.');
		} else {
			throw new Error(`Request error: ${error.message}`);
		}
	}
};

/**
 * Accept a recurring notification and create actual income/expense
 * @param {string} notificationId - UUID of the notification
 * @param {string} language - Language code for Accept-Language sidebar-header (en, hy, ru)
 * @returns {Promise<Object>} Created income/expense object
 */
export const acceptRecurringNotification = async (
	notificationId,
	language = 'en',
) => {
	try {
		const response = await axiosInstance.post(
			`recurring-notifications/${notificationId}/accept`,
			{},
			{
				withCredentials: true,
				headers: {
					Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
					'Accept-Language': language,
					'Content-Type': 'application/json',
				},
			},
		);

		return response.data;
	} catch (error) {
		console.error('Error accepting notification:', error);
		if (error.response) {
			const errorMessage =
				error.response.data?.detail ||
				error.response.data?.message ||
				`Server error: ${error.response.status}` ||
				'Failed to accept notification';
			throw new Error(errorMessage);
		} else if (error.request) {
			throw new Error('No response from server. Please check your connection.');
		} else {
			throw new Error(`Request error: ${error.message}`);
		}
	}
};

/**
 * Mark a recurring notification as accepted without creating income/expense.
 * Use after the frontend has created the record via the normal create API.
 * @param {string} notificationId - UUID of the notification
 * @param {string} language - Language code for Accept-Language sidebar-header (en, hy, ru)
 * @returns {Promise<Object>} Success response
 */
export const acceptRecurringNotificationSkipCreate = async (
	notificationId,
	language = 'en',
) => {
	try {
		const response = await axiosInstance.post(
			`recurring-notifications/${notificationId}/accept`,
			{ skip_create: true },
			{
				withCredentials: true,
				headers: {
					Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
					'Accept-Language': language,
					'Content-Type': 'application/json',
				},
			},
		);

		return response.data;
	} catch (error) {
		console.error('Error marking notification as accepted:', error);
		if (error.response) {
			const errorMessage =
				error.response.data?.detail ||
				error.response.data?.message ||
				`Server error: ${error.response.status}` ||
				'Failed to mark notification as accepted';
			throw new Error(errorMessage);
		} else if (error.request) {
			throw new Error('No response from server. Please check your connection.');
		} else {
			throw new Error(`Request error: ${error.message}`);
		}
	}
};

/**
 * Decline a recurring notification
 * @param {string} notificationId - UUID of the notification
 * @param {string} language - Language code for Accept-Language sidebar-header (en, hy, ru)
 * @returns {Promise<{message: string}>} Success message
 */
export const declineRecurringNotification = async (
	notificationId,
	language = 'en',
) => {
	try {
		const response = await axiosInstance.post(
			`recurring-notifications/${notificationId}/decline`,
			{},
			{
				withCredentials: true,
				headers: {
					Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
					'Accept-Language': language,
					'Content-Type': 'application/json',
				},
			},
		);

		return response.data;
	} catch (error) {
		console.error('Error declining notification:', error);
		if (error.response) {
			const errorMessage =
				error.response.data?.detail ||
				error.response.data?.message ||
				`Server error: ${error.response.status}` ||
				'Failed to decline notification';
			throw new Error(errorMessage);
		} else if (error.request) {
			throw new Error('No response from server. Please check your connection.');
		} else {
			throw new Error(`Request error: ${error.message}`);
		}
	}
};

/**
 * Mark a goal notification as read
 * @param {string} notificationId - UUID of the notification
 * @param {string} language - Language code for Accept-Language sidebar-header (en, hy, ru)
 * @returns {Promise<{message: string}>} Success message
 */
export const markGoalNotificationRead = async (
	notificationId,
	language = 'en',
) => {
	try {
		const response = await axiosInstance.post(
			`goal-notifications/${notificationId}/read`,
			null,
			{
				withCredentials: true,
				headers: {
					Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
					'Accept-Language': language,
				},
			},
		);

		return response.data;
	} catch (error) {
		console.error('Error marking notification as read:', error);
		if (error.response) {
			const errorMessage =
				error.response.data?.detail ||
				error.response.data?.message ||
				`Server error: ${error.response.status}` ||
				'Failed to mark notification as read';
			throw new Error(errorMessage);
		} else if (error.request) {
			throw new Error('No response from server. Please check your connection.');
		} else {
			throw new Error(`Request error: ${error.message}`);
		}
	}
};
